/**
 * Unified Dashboard Store
 *
 * This store centralizes all data fetching and provides a merged, unified view
 * of patients/appointments across all data sources:
 *
 * DATA SOURCE HIERARCHY:
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  🥇 PRIMARY SOURCES (Source of Truth)                                   │
 * │  ├── MRrealtimeview3c  → What's actually happening in MediRecords       │
 * │  └── Task Router API   → Real-time queue stats & task details from      │
 * │                          Twilio TaskRouter (getTaskRouterDetails)       │
 * │                                                                          │
 * │  🥈 SECONDARY SOURCES (Supplementary Data)                               │
 * │  └── TwilioES          → Active call history from [twi].[TwilioES]      │
 * │                          table (getActiveCalls) - enriches with         │
 * │                          patients array, agent info, etc.               │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * MERGE STRATEGY:
 * 1. Realtime records (MRrealtimeview3c) are the SOURCE OF TRUTH for
 *    waiting room / appointment status
 *    - Patient identity parsed from realtime note (Pat/Dob)
 *    - Matched with Active Calls via patient name + DOB
 *
 * 2. Task Router (getTaskRouterDetails) is the SOURCE OF TRUTH for
 *    queue statistics and active task states
 *    - Queue-level stats (totalTasks, tasksByStatus, etc.)
 *    - Task details (assignmentStatus, age, taskAttributes, etc.)
 *
 * 3. Active Calls (getActiveCalls/TwilioES) provide SUPPLEMENTARY data
 *    - Enriches tasks with patients array, agentName, startTime, etc.
 *    - Matched with Task Router tasks via taskSid
 *
 * 4. MR appointments are NOT used in the Unified Dashboard
 */

import { create } from 'zustand';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  fetchAllUnifiedData,
  fetchTaskRouter,
} from '../utils/fetchUnifiedData';
import { parseFieldSetup } from '../utils/stringUtils';
import {
  parseCallPatients,
  parseTaskAttributes,
  extractSiteFromCall,
  translateCallStatus,
  ACTIVE_CALL_STATUSES,
} from '../utils/callUtils';

dayjs.extend(utc);

// ============================================================================
// SLA Configuration (from RealtimeWaitingRoom.jsx)
// ============================================================================

const SLA_RULES = {
  default: {
    1: 5,
    2: 15,
    3: 120,
    4: 120,
    5: 120,
    default: 120,
  },
  URGHosp: {
    1: 5,
    2: 10,
    3: 60,
    4: 120,
    5: 120,
    default: 60,
  },
  'NSW Ambulance': {
    1: 5,
    2: 10,
    3: 60,
    4: 120,
    5: 120,
    default: 60,
  },
  'Ambulance Victoria': {
    1: 5,
    2: 10,
    3: 60,
    4: 120,
    5: 120,
    default: 60,
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse triage category string to number
 */
const parseTriageCategory = (trcat) => {
  if (!trcat) return null;
  const matches = String(trcat).match(/\d+/g);
  if (!matches || matches.length === 0) return null;
  const nums = matches.map((n) => Number(n)).filter((n) => !Number.isNaN(n));
  return nums.length ? Math.min(...nums) : null;
};

/**
 * Get SLA minutes based on client and triage category
 */
const getSlaMinutes = (client, trcatNum) => {
  const rules = SLA_RULES[client] || SLA_RULES.default;
  if (Number.isFinite(trcatNum)) {
    return rules[trcatNum] ?? rules.default ?? 120;
  }
  return rules.default ?? 120;
};

/**
 * Get wait time in minutes from Realtime data ONLY
 *
 * Wait time should ONLY come from the Realtime table (MRrealtimeview3c),
 * which is the source of truth. No fallback calculations.
 */
const getRealtimeWaitMinutes = (row) => {
  // Only use explicit wait time fields from Realtime data
  const explicit =
    row.waittime ??
    row.waitTime ??
    (Number.isFinite(row.wait) ? row.wait : null);

  if (Number.isFinite(explicit)) return Number(explicit);

  return null; // No fallback - wait time must come from Realtime
};

/**
 * Get row background color based on SLA percentage
 */
const getRowColor = (waitMinutes, slaMinutes) => {
  if (
    !Number.isFinite(waitMinutes) ||
    !Number.isFinite(slaMinutes) ||
    slaMinutes <= 0
  ) {
    return null; // No color if SLA can't be calculated
  }

  const ratio = waitMinutes / slaMinutes;

  if (ratio >= 1.0) return '#ffebee'; // Red - SLA breached
  if (ratio >= 0.8) return '#fff3e0'; // Orange - Critical (80-100%)
  if (ratio >= 0.5) return '#fffde7'; // Yellow - Warning (50-80%)
  return null; // No color - OK (<50%)
};

/**
 * Get SLA status label and color for display
 */
const getSlaStatus = (waitMinutes, slaMinutes) => {
  if (
    !Number.isFinite(waitMinutes) ||
    !Number.isFinite(slaMinutes) ||
    slaMinutes <= 0
  ) {
    return { label: 'No SLA', color: '#595959', percentage: null };
  }

  const percentage = Math.round((waitMinutes / slaMinutes) * 100);

  if (percentage >= 100)
    return { label: 'Breached', color: '#ff4d4f', percentage };
  if (percentage >= 80)
    return { label: 'Critical', color: '#fa8c16', percentage };
  if (percentage >= 50)
    return { label: 'Warning', color: '#fadb14', percentage };
  return { label: 'OK', color: '#52c41a', percentage };
};

/**
 * Normalize appointment status for consistent comparisons
 */
const normalizeAppointmentStatus = (status) =>
  String(status || '')
    .toLowerCase()
    .trim();

/**
 * Check whether a record is still in the waiting room
 */
const isWaitingRoomStatus = (status) =>
  normalizeAppointmentStatus(status) === 'waiting room';

/**
 * Check whether a record is with a provider/doctor
 */
const isWithProviderStatus = (status) => {
  const normalized = normalizeAppointmentStatus(status);
  return normalized.includes('provider') || normalized.includes('doctor');
};

/**
 * Calculate priority score for sorting (higher = more urgent)
 */
const calculatePriorityScore = (record) => {
  let score = 0;

  // Active calls get higher priority
  if (record.hasActiveCall) {
    score += 1000;

    // Call status priority
    switch (record.call?.status) {
      case 'created':
        score += 500; // In queue, waiting
        break;
      case 'reserved':
        score += 400; // Being handled
        break;
      case 'wrapping':
        score += 300;
        break;
      default:
        break;
    }
  }

  // SLA breach increases priority
  if (record.slaStatus?.percentage) {
    score += Math.min(record.slaStatus.percentage, 200);
  }

  // Triage category (lower = more urgent)
  if (record.appointment?.triageCategoryNum) {
    score += (6 - record.appointment.triageCategoryNum) * 50;
  }

  // Wait time contributes to priority
  if (record.waitingRoom?.waitTime) {
    score += Math.min(record.waitingRoom.waitTime, 100);
  }

  return score;
};

/**
 * Normalize patient name for matching (lowercase, trimmed)
 */
const normalizePatientName = (name) => {
  if (!name) return '';
  return parseFieldSetup(String(name)).toLowerCase().trim();
};

/**
 * Normalize DOB for matching (YYYY-MM-DD format)
 */
const normalizeDob = (dob) => {
  if (!dob) return '';
  const parsed = dayjs(dob);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

/**
 * Generate a unique key for patient matching
 */
const generatePatientMatchKey = (firstName, lastName, dob) => {
  const fn = normalizePatientName(firstName);
  const ln = normalizePatientName(lastName);
  const d = normalizeDob(dob);

  if (!fn || !ln || !d) return null;

  // Skip invalid entries with date-like names
  if (
    fn.startsWith('2025-') ||
    fn.startsWith('2026-') ||
    ln.startsWith('2025-') ||
    ln.startsWith('2026-')
  ) {
    return null;
  }

  return `${fn}|${ln}|${d}`;
};

/**
 * Extract patient details from realtime note text
 */
const extractPatientFromRealtimeNote = (note) => {
  if (!note) {
    return { firstName: null, lastName: null, dob: null, phone: null };
  }

  const text = String(note);

  const nameMatch = text.match(
    /\b(?:Pat|Patient)\s*:\s*([^,\n]+?)(?=\s*(?:Dob|DOB)\s*:|,|\n|$)/i
  );
  let firstName = null;
  let lastName = null;
  if (nameMatch?.[1]) {
    const name = nameMatch[1].trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        firstName = parts[0];
      } else if (parts.length > 1) {
        firstName = parts[0];
        lastName = parts.slice(1).join(' ');
      }
    }
  }

  const dobMatch =
    text.match(/\b(?:Dob|DOB)\s*:\s*([0-9]{4}-[0-9]{2}-[0-9]{2})/i) ||
    text.match(/\b(?:Dob|DOB)\s*:\s*([0-9]{2}\/[0-9]{2}\/[0-9]{2,4})/i);
  const dob = dobMatch?.[1] ? dobMatch[1].trim() : null;

  const phoneMatch = text.match(
    /\b(?:callerPhone|PHNumber|Phone|phone)\s*:?\s*([+0-9][0-9\s\-()]{5,})/i
  );
  let phone = phoneMatch?.[1] ? phoneMatch[1].split(',')[0].trim() : null;
  if (phone) {
    phone = phone.replace(/[^\d+]/g, '');
  }

  return { firstName, lastName, dob, phone };
};

// parseCallPatients is imported from '../utils/callUtils'

// translateCallStatus is imported from '../utils/callUtils'

/**
 * Determine overall status for display
 */
const determineOverallStatus = (record) => {
  // If has active call, use call status
  if (record.hasActiveCall && record.call) {
    return translateCallStatus(record.call.status);
  }

  // Use appointment status (if available)
  if (record.appointment) {
    return record.appointment.status || 'Unknown';
  }

  // Use waiting room status
  if (record.hasRealtimeData && record.waitingRoom) {
    return record.waitingRoom.apptStatus || 'Waiting';
  }

  return 'Unknown';
};

// ============================================================================
// Data Merging Logic
// ============================================================================

/**
 * Merge all data sources into unified records
 *
 * STRATEGY (Realtime as Source of Truth):
 *
 * 1. Build lookup map for efficient matching:
 *    - Realtime records by patient key (parsed from note)
 *
 * 2. Process Realtime records (PRIMARY - source of truth):
 *    - Each Realtime record becomes a unified record
 *    - Generate patient match key for later Active Call matching
 *
 * 3. Process Active Calls:
 *    - Try to match with Realtime records via patient name + DOB
 *    - If matched: enrich the existing Realtime-based record with call data
 *    - If no match: create call-only record
 *
 * 4. DO NOT add MR-only records:
 *    - If an MR appointment has no Realtime record, it's not active
 *    - These are completed/cancelled appointments
 */
const mergeAllData = (
  activeCalls,
  _mrAppointments,
  realtimeView,
  taskRouter
) => {
  const unifiedRecords = [];
  const processedRealtimeGuids = new Set();
  const matchedCallTaskSids = new Set();

  // -------------------------------------------------------------------------
  // Step 0: Enrich TwilioES records with TaskRouter's authoritative status
  // -------------------------------------------------------------------------
  // TwilioES (Event Stream) often has stale/incorrect currentStatus for
  // video calls (e.g. "created" instead of "pending", "reserved" instead of
  // "assigned"). TaskRouter is the real-time source of truth for task status.
  const taskRouterStatusMap = new Map();
  const taskRouterQueueMap = new Map();
  (taskRouter?.tasks || []).forEach((task) => {
    if (task.sid && task.assignmentStatus) {
      taskRouterStatusMap.set(task.sid, task.assignmentStatus);
    }
    if (task.sid && task.taskQueueFriendlyName) {
      taskRouterQueueMap.set(task.sid, task.taskQueueFriendlyName);
    }
  });

  const enrichedActiveCalls = (activeCalls || []).map((call) => {
    if (!call.taskSid) return call;
    const trStatus = taskRouterStatusMap.get(call.taskSid);
    if (trStatus && trStatus !== call.currentStatus) {
      return { ...call, currentStatus: trStatus };
    }
    return call;
  });

  // ACTIVE_CALL_STATUSES imported from '../utils/callUtils'
  // Filter active calls to only include valid statuses
  const filteredActiveCalls = enrichedActiveCalls.filter((call) =>
    ACTIVE_CALL_STATUSES.includes(call.currentStatus)
  );

  // -------------------------------------------------------------------------
  // Step 1: Build lookup maps
  // -------------------------------------------------------------------------

  // Build a map to match Realtime records with Active Calls later
  // Key: patient match key, Value: realtime record with enriched data
  const realtimeByPatientKey = new Map();

  // Build a map of Active Calls by fnote/siteCode for fallback matching
  // Key: siteCode (lowercase), Value: {call, patients}
  // Note: We use ALL active calls here (not just filtered by status) because
  // we want patient data from any call that might match by siteCode
  const activeCallsBySiteCode = new Map();
  (activeCalls || []).forEach((call) => {
    const siteCode = extractSiteFromCall(call);
    if (siteCode) {
      const key = siteCode.toLowerCase();
      // Store the first call for each site with valid patient data
      if (!activeCallsBySiteCode.has(key)) {
        const callPatients = parseCallPatients(call);
        if (callPatients.length > 0) {
          activeCallsBySiteCode.set(key, { call, patients: callPatients });
        }
      }
    }
  });

  // -------------------------------------------------------------------------
  // Step 2: Process Realtime Records (PRIMARY - Source of Truth)
  // -------------------------------------------------------------------------

  (realtimeView || []).forEach((rt) => {
    if (!rt.appointmentguid) return;

    // Use patient2 data from the API (most reliable source)
    const notePatient = {
      firstName: rt.givenName || null,
      lastName: rt.familyName || null,
      dob: rt.birthDate ? normalizeDob(rt.birthDate) : null,
      phone: extractPatientFromRealtimeNote(rt.finalappnote).phone || null,
    };

    // // Old fallback logic - commented out, using patient2 data directly instead
    // let notePatient = extractPatientFromRealtimeNote(rt.finalappnote);
    //
    // // Fallback: Try to get from Active Calls by siteCode
    // if (!notePatient.firstName && !notePatient.lastName && rt.fnote) {
    //   const siteKey = rt.fnote.toLowerCase();
    //   const callData = activeCallsBySiteCode.get(siteKey);
    //   if (callData && callData.patients && callData.patients.length > 0) {
    //     const firstPatient = callData.patients[0];
    //     if (firstPatient.patientFirstName || firstPatient.patientLastName) {
    //       notePatient = {
    //         firstName: firstPatient.patientFirstName || null,
    //         lastName: firstPatient.patientLastName || null,
    //         dob: firstPatient.dob || null,
    //         phone: null,
    //       };
    //     }
    //   }
    // }

    const patientKey = generatePatientMatchKey(
      notePatient.firstName,
      notePatient.lastName,
      notePatient.dob
    );

    // Store for later Active Call matching
    if (patientKey) {
      realtimeByPatientKey.set(patientKey, {
        realtimeData: rt,
        patientKey,
        notePatient,
      });
    }

    // Create the unified record from Realtime (source of truth)
    const record = createRealtimeBasedRecord(rt, notePatient);
    if (record) {
      unifiedRecords.push(record);
      processedRealtimeGuids.add(rt.appointmentguid);
    }
  });

  // -------------------------------------------------------------------------
  // Step 2.5: Cross-check Active Calls against TaskRouter + Realtime
  // Filter out stale calls from TwilioES that no longer exist in either source.
  // If a call's taskSid is not in TaskRouter AND its patient is not in Realtime,
  // it means the EventStream data was not properly cleaned up → filter it out.
  // -------------------------------------------------------------------------

  const taskRouterTaskSids = new Set(
    (taskRouter?.tasks || []).map((t) => t.sid)
  );

  const validActiveCalls = filteredActiveCalls.filter((call) => {
    // Check 1: taskSid exists in TaskRouter → definitely active
    if (call.taskSid && taskRouterTaskSids.has(call.taskSid)) {
      return true;
    }

    // Check 2: taskSid not in TaskRouter → check if patient exists in Realtime
    const callPatients = parseCallPatients(call);
    if (callPatients.length === 0) {
      // No patient info to cross-check → keep for safety
      return true;
    }

    // Check if ANY patient from this call exists in realtimeView
    for (const patient of callPatients) {
      const patientKey = generatePatientMatchKey(
        patient.patientFirstName,
        patient.patientLastName,
        patient.dob
      );
      if (patientKey && realtimeByPatientKey.has(patientKey)) {
        return true; // Patient found in realtime → keep
      }
    }

    // Not in TaskRouter, not in Realtime → stale, filter out
    console.log(
      '[mergeAllData] Filtering stale call:',
      call.taskSid,
      callPatients.map((p) => `${p.patientFirstName} ${p.patientLastName}`)
    );
    return false;
  });

  // -------------------------------------------------------------------------
  // Step 3: Process Active Calls (filtered to valid statuses)
  // -------------------------------------------------------------------------

  validActiveCalls.forEach((call) => {
    const callPatients = parseCallPatients(call);

    if (callPatients.length === 0) {
      // Call without patient data - create call-only record
      const record = createCallOnlyRecord(call);
      if (record) {
        unifiedRecords.push(record);
        matchedCallTaskSids.add(call.taskSid);
      }
    } else {
      // Process each patient in the call
      callPatients.forEach((patient, index) => {
        const patientKey = generatePatientMatchKey(
          patient.patientFirstName,
          patient.patientLastName,
          patient.dob
        );

        // Try to find matching Realtime record
        const realtimeMatch = patientKey
          ? realtimeByPatientKey.get(patientKey)
          : null;

        if (realtimeMatch) {
          // Found Realtime match - enrich the existing record with call data
          // Find the existing record and add call data to it
          const existingRecord = unifiedRecords.find(
            (r) => r.id === realtimeMatch.realtimeData.appointmentguid
          );

          if (existingRecord && !existingRecord.hasActiveCall) {
            // Enrich with call data
            existingRecord.hasActiveCall = true;
            existingRecord.call = {
              taskSid: call.taskSid,
              caseId: patient.caseID || call.caseID,
              callType: call.callType,
              status: call.currentStatus,
              agentName: call.agentName,
              agentEmail: call.agentEmail,
              startTime: call.startTime,
              callStep: call.callStep,
              isMultiPatient: callPatients.length > 1,
            };

            // Also update patient info from Active Call if missing in Realtime
            if (
              !existingRecord.patient?.firstName &&
              patient.patientFirstName
            ) {
              existingRecord.patient = existingRecord.patient || {};
              existingRecord.patient.firstName = patient.patientFirstName;
            }
            if (!existingRecord.patient?.lastName && patient.patientLastName) {
              existingRecord.patient = existingRecord.patient || {};
              existingRecord.patient.lastName = patient.patientLastName;
            }
            if (!existingRecord.patient?.dob && patient.dob) {
              existingRecord.patient = existingRecord.patient || {};
              existingRecord.patient.dob = patient.dob;
            }

            existingRecord.overallStatus =
              determineOverallStatus(existingRecord);
            existingRecord.priorityScore =
              calculatePriorityScore(existingRecord);
          }
          matchedCallTaskSids.add(call.taskSid);
        } else {
          // No Realtime match - create call-only record
          const record = createCallOnlyRecord(call, patient, {
            isMultiPatient: callPatients.length > 1,
            patientIndex: index,
          });

          if (record) {
            unifiedRecords.push(record);
            matchedCallTaskSids.add(call.taskSid);
          }
        }
      });
    }
  });

  // -------------------------------------------------------------------------
  // Step 4: Do not add appointment-only records
  // -------------------------------------------------------------------------
  // Only Realtime and Active Call records are shown in the unified dashboard

  // -------------------------------------------------------------------------
  // Step 5: Sort by priority and add indexes
  // -------------------------------------------------------------------------

  // Attach queue name from Task Router for GP/Specialist filtering
  const enrichedRecords = unifiedRecords.map((record) => ({
    ...record,
    queueName: record.call?.taskSid
      ? taskRouterQueueMap.get(record.call.taskSid) || null
      : null,
  }));

  enrichedRecords.sort((a, b) => b.priorityScore - a.priorityScore);

  // -------------------------------------------------------------------------
  // Step 6: Group multi-patient call patients together
  // -------------------------------------------------------------------------
  // After priority sort, ensure patients from same call (taskSid) stay together
  // Move all patients of a multi-patient call to where the first one appears

  const groupedRecords = [];
  const processedTaskSids = new Set();

  enrichedRecords.forEach((record) => {
    const taskSid = record.call?.taskSid;
    const isMultiPatient = record.call?.isMultiPatient;

    // If this is a multi-patient call and we haven't processed this taskSid yet
    if (isMultiPatient && taskSid && !processedTaskSids.has(taskSid)) {
      // Find all records with the same taskSid and add them together
      const sameCallRecords = enrichedRecords.filter(
        (r) => r.call?.taskSid === taskSid
      );
      groupedRecords.push(...sameCallRecords);
      processedTaskSids.add(taskSid);
    } else if (!isMultiPatient || !taskSid) {
      // Not a multi-patient call, add as-is
      groupedRecords.push(record);
    }
    // If already processed (multi-patient with processed taskSid), skip
  });

  // Add sequential index for display
  groupedRecords.forEach((record, index) => {
    record.displayIndex = index + 1;
  });

  return groupedRecords;
};

/**
 * Create a unified record from Realtime data (source of truth)
 * Patient details are parsed from realtime note (Pat/Dob)
 */
const createRealtimeBasedRecord = (realtimeData, notePatient = {}) => {
  if (!realtimeData?.appointmentguid) return null;

  const hasRealtimeData = true;
  const hasMRAppointment = true; // Appointment data exists via realtime view

  // ID comes from Realtime (source of truth)
  const id = realtimeData.appointmentguid;

  // Patient info - parsed from realtime note
  const patientInfo = {
    firstName: notePatient.firstName || null,
    lastName: notePatient.lastName || null,
    dob: notePatient.dob || null,
    phone: notePatient.phone || null,
  };

  // Site info - Realtime is source of truth
  const siteInfo = {
    code: realtimeData.fnote || null,
    client: realtimeData.name || null,
  };

  // Triage category - Realtime is source of truth
  const trcatRaw =
    realtimeData.trCAT || realtimeData.trcat || realtimeData.trCat;
  const trcatNum = parseTriageCategory(trcatRaw);

  // Appointment data - from Realtime (truth)
  const appointmentData = {
    guid: realtimeData.appointmentguid,
    status: realtimeData['Appt status'] || null,
    triageCategory: trcatRaw,
    triageCategoryNum: trcatNum,
    urgency: realtimeData.urgent === 2 ? 'Urgent' : 'Standard',
    createdAt: realtimeData.appointcreatedatetime || null,
    note: realtimeData.finalappnote || null,
  };

  // Waiting room data - all from Realtime (source of truth)
  const slaMinutes = getSlaMinutes(siteInfo.client, trcatNum);
  const waitMinutes = getRealtimeWaitMinutes(realtimeData);

  const waitingRoomData = {
    waitTime: waitMinutes,
    slaMinutes,
    clockStatus: realtimeData['Clock status'] || null,
    stopReason: realtimeData['Stop Reason'] || null,
    apptStatus: realtimeData['Appt status'] || null,
    doctorName: realtimeData.username || null,
    elapsedTime: realtimeData['Elapsed time'] || null,
    firstAppearInWaiting: realtimeData.firstappearinwaiting || null,
    withDoctorDateTime: realtimeData.withdoctordatetime || null,
  };

  // SLA status
  const slaStatus = getSlaStatus(waitMinutes, slaMinutes);

  // Row color based on SLA
  const rowColor = getRowColor(waitMinutes, slaMinutes);

  const record = {
    id,
    hasActiveCall: false, // Will be set to true if matched with Active Call
    hasMRAppointment,
    hasRealtimeData,
    patient: patientInfo,
    site: siteInfo,
    appointment: appointmentData,
    waitingRoom: waitingRoomData,
    call: null, // Will be populated if matched with Active Call
    slaStatus,
    rowColor,
    overallStatus: realtimeData['Appt status'] || 'Unknown',
    priorityScore: 0,
  };

  record.priorityScore = calculatePriorityScore(record);

  return record;
};

/**
 * Check if a value looks like a valid patient name (not a timestamp or other invalid data)
 */
const isValidPatientName = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  // Reject if it looks like a timestamp (ISO format or similar)
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return false;
  if (/^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) return false;
  if (/T\d{2}:\d{2}:\d{2}/.test(trimmed)) return false;

  // Reject if it's just numbers
  if (/^[\d\s\-./]+$/.test(trimmed)) return false;

  return true;
};

/**
 * Create a record for call-only (no Realtime match)
 * Uses patient data from call only, with multiple fallback sources
 *
 * @param {Object} call - Active call data
 * @param {Object} patient - Patient data from call (optional)
 * @param {Object} options - Additional options
 * @param {boolean} options.isMultiPatient - Whether call has multiple patients
 * @param {number} options.patientIndex - Index of patient in multi-patient call
 */
const createCallOnlyRecord = (call, patient = null, options = {}) => {
  const { isMultiPatient = false, patientIndex = 0 } = options;

  // Extract site from task attributes if available
  const siteCode = extractSiteFromCall(call) || null;

  // Patient info - try multiple sources with validation
  let firstName = null;
  let lastName = null;
  let dob = null;

  // Source 1: patient parameter (from parseCallPatients)
  if (patient) {
    if (isValidPatientName(patient.patientFirstName)) {
      firstName = patient.patientFirstName;
    }
    if (isValidPatientName(patient.patientLastName)) {
      lastName = patient.patientLastName;
    }
    if (patient.dob && !patient.dob.includes('T')) {
      // Validate DOB is not a timestamp
      dob = patient.dob;
    }
  }

  // Source 2: direct fields on call object (fallback)
  if (!firstName && isValidPatientName(call.patientFirstName)) {
    firstName = call.patientFirstName;
  }
  if (!lastName && isValidPatientName(call.patientLastName)) {
    lastName = call.patientLastName;
  }
  if (!dob && call.dob && !call.dob.includes('T')) {
    dob = call.dob;
  }

  // Source 3: taskAttributes (fallback)
  const attrs = parseTaskAttributes(call.taskAttributes);
  if (!firstName && !lastName && isValidPatientName(attrs.name)) {
    const nameParts = attrs.name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(' ');
    } else {
      firstName = attrs.name;
    }
  }

  // Source 4: videoCallerName from taskAttributes (fallback)
  if (!firstName && !lastName && isValidPatientName(attrs.videoCallerName)) {
    const nameParts = attrs.videoCallerName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(' ');
    } else {
      firstName = attrs.videoCallerName;
    }
  }

  const patientInfo = {
    firstName,
    lastName,
    dob,
    phone: null,
  };

  // Site info
  const siteInfo = {
    code: siteCode,
    client: null,
  };

  const trcatNum = null;
  const slaMinutes = getSlaMinutes(siteInfo.client, trcatNum) || 120;
  // Call-only records have NO wait time (only Realtime is source of truth)
  const waitMinutes = null;

  // Generate unique ID
  const id = `call-${call.taskSid}-${patientIndex}`;

  const record = {
    id,
    hasActiveCall: true,
    hasMRAppointment: false,
    hasRealtimeData: false,
    patient: patientInfo,
    site: siteInfo,
    appointment: null,
    waitingRoom: {
      waitTime: null, // No wait time for call-only records
      slaMinutes,
      clockStatus: null,
      stopReason: null,
      apptStatus: null,
      doctorName: null,
      elapsedTime: null,
    },
    call: {
      taskSid: call.taskSid,
      caseId: patient?.caseID || call.caseID,
      callType: call.callType,
      status: call.currentStatus,
      agentName: call.agentName,
      agentEmail: call.agentEmail,
      startTime: call.startTime,
      callStep: call.callStep,
      isMultiPatient,
    },
    slaStatus: trcatNum
      ? getSlaStatus(waitMinutes, slaMinutes)
      : { label: 'No SLA', color: '#595959', percentage: null },
    rowColor: trcatNum ? getRowColor(waitMinutes, slaMinutes) : null,
    overallStatus: translateCallStatus(call.currentStatus),
    priorityScore: 0,
  };

  record.priorityScore = calculatePriorityScore(record);

  return record;
};

// extractSiteFromCall is imported from '../utils/callUtils'

// ============================================================================
// Process Task Router Data for Queue Info Panel
// ============================================================================

const processQueueData = (taskRouter, activeCalls, unifiedRecords) => {
  if (!taskRouter?.queues) return [];

  // ACTIVE_CALL_STATUSES imported from '../utils/callUtils'
  // Build a map of activeCalls by taskSid for quick lookup
  const activeCallsByTaskSid = new Map();
  (activeCalls || []).forEach((call) => {
    if (call.taskSid) {
      activeCallsByTaskSid.set(call.taskSid, call);
    }
  });

  // Build a map of unified records by taskSid for wait time lookup
  // This allows us to use Realtime wait time as source of truth
  const unifiedRecordsByTaskSid = new Map();
  (unifiedRecords || []).forEach((record) => {
    if (record.call?.taskSid) {
      // Store the first record for this taskSid (for multi-patient calls, all share same wait time)
      if (!unifiedRecordsByTaskSid.has(record.call.taskSid)) {
        unifiedRecordsByTaskSid.set(record.call.taskSid, record);
      }
    }
  });

  // Group tasks from taskRouter by queue (primary source - more reliable)
  const tasksByQueue = new Map();
  (taskRouter.tasks || [])
    .filter((task) => ACTIVE_CALL_STATUSES.includes(task.assignmentStatus))
    .forEach((task) => {
      const queueName = task.taskQueueFriendlyName;
      if (queueName) {
        if (!tasksByQueue.has(queueName)) {
          tasksByQueue.set(queueName, []);
        }
        // Merge with activeCall data if available (for patients field)
        const activeCall = activeCallsByTaskSid.get(task.sid);
        // Get unified record for Realtime wait time (source of truth)
        const unifiedRecord = unifiedRecordsByTaskSid.get(task.sid);

        tasksByQueue.get(queueName).push({
          // Task Router data (primary)
          taskSid: task.sid,
          assignmentStatus: task.assignmentStatus,
          currentStatus: task.assignmentStatus,
          age: task.age,
          dateCreated: task.dateCreated,
          taskAttributes: task.attributes,
          taskQueueFriendlyName: task.taskQueueFriendlyName,
          // Active Call data (supplementary - may have patients field)
          patients: activeCall?.patients || null,
          agentName: activeCall?.agentName || null,
          startTime: activeCall?.startTime || task.dateCreated,
          callStep: activeCall?.callStep || task.taskQueueFriendlyName,
          // Realtime data (source of truth for wait time)
          realtimeWaitTime: unifiedRecord?.waitingRoom?.waitTime ?? null,
          realtimeElapsedTime: unifiedRecord?.waitingRoom?.elapsedTime ?? null,
          hasRealtimeData: unifiedRecord?.hasRealtimeData ?? false,
        });
      }
    });

  return taskRouter.queues
    .map((queue) => {
      const patients = tasksByQueue.get(queue.friendlyName) || [];

      // Derive task counts from actual task entries (not queue stats) to ensure
      // badge and status tags always match the visible patient entries.
      // Queue stats (queue.stats.tasksByStatus) can be out of sync with the
      // individual task list due to timing gaps in the API.
      const tasksByStatus = {};
      patients.forEach((task) => {
        const status = task.currentStatus;
        if (status) {
          tasksByStatus[status] = (tasksByStatus[status] || 0) + 1;
        }
      });

      return {
        sid: queue.sid,
        name: queue.friendlyName,
        totalTasks: patients.length,
        tasksByStatus,
        availableWorkers: queue.stats?.totalAvailableWorkers || 0,
        eligibleWorkers: queue.stats?.totalEligibleWorkers || 0,
        longestWait: queue.stats?.longestTaskWaitingAge || 0,
        patients,
      };
    })
    .filter((queue) => queue.totalTasks > 0 || queue.availableWorkers > 0)
    .sort((a, b) => b.totalTasks - a.totalTasks);
};

// ============================================================================
// Zustand Store
// ============================================================================

const useUnifiedDashboardStore = create((set, get) => ({
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  // Raw data from APIs
  rawActiveCalls: [],
  rawMRAppointments: [],
  rawRealtimeView: [],
  rawTaskRouter: {},

  // Processed/merged data
  unifiedRecords: [],
  queueInfo: [],

  // Fetch state
  isFetching: false,
  isFetchingTaskRouter: false,
  fetchError: null,
  lastFetchedAt: null,

  // Auto-refresh
  refreshInterval: null,
  refreshIntervalMs: 60000, // 1 minute default

  // Panel visibility
  isWaitingRoomTableVisible: true, // Controls the table inside WaitingRoomPanel
  activePanelTab: 'waitingRoom', // 'waitingRoom' | 'queueInfo'

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Fetch all data and merge into unified records
   */
  fetchUnifiedData: async (options = {}) => {
    set({ isFetching: true, fetchError: null });

    try {
      const {
        activeCalls,
        mrAppointments,
        realtimeView,
        taskRouter,
        fetchedAt,
      } = await fetchAllUnifiedData(options);

      // Merge all data
      const unifiedRecords = mergeAllData(
        activeCalls,
        mrAppointments,
        realtimeView,
        taskRouter
      );

      // Process queue data (pass unifiedRecords for Realtime wait time lookup)
      const queueInfo = processQueueData(
        taskRouter,
        activeCalls,
        unifiedRecords
      );

      set({
        rawActiveCalls: activeCalls,
        rawMRAppointments: mrAppointments,
        rawRealtimeView: realtimeView,
        rawTaskRouter: taskRouter,
        unifiedRecords,
        queueInfo,
        lastFetchedAt: fetchedAt,
        isFetching: false,
      });

      console.log('[unifiedDashboardStore] Data fetched and merged:', {
        activeCalls: activeCalls.length,
        mrAppointments: mrAppointments.length,
        realtimeView: realtimeView.length,
        unifiedRecords: unifiedRecords.length,
        queues: queueInfo.length,
      });
    } catch (error) {
      console.error('[unifiedDashboardStore] Fetch error:', error);
      set({
        isFetching: false,
        fetchError: error.message || 'Failed to fetch data',
      });
    }
  },

  /**
   * Start auto-refresh interval
   */
  startAutoRefresh: (intervalMs) => {
    const { stopAutoRefresh, fetchUnifiedData } = get();

    // Clear existing interval
    stopAutoRefresh();

    const ms = intervalMs || get().refreshIntervalMs;

    const intervalId = setInterval(() => {
      fetchUnifiedData();
    }, ms);

    set({ refreshInterval: intervalId, refreshIntervalMs: ms });

    console.log(`[unifiedDashboardStore] Auto-refresh started (${ms}ms)`);
  },

  /**
   * Stop auto-refresh interval
   */
  stopAutoRefresh: () => {
    const { refreshInterval } = get();
    if (refreshInterval) {
      clearInterval(refreshInterval);
      set({ refreshInterval: null });
      console.log('[unifiedDashboardStore] Auto-refresh stopped');
    }
  },

  /**
   * Fetch immediately and restart the auto-refresh timer.
   * Use after a write operation (e.g. priority change) so the
   * UI updates right away and the 60s countdown resets.
   */
  fetchAndResetTimer: async () => {
    const { fetchUnifiedData, startAutoRefresh, refreshIntervalMs } = get();
    await fetchUnifiedData();
    startAutoRefresh(refreshIntervalMs);
  },

  /**
   * Fetch only the TaskRouter API and update video room / queue data.
   * Lighter than a full refresh — useful for the Video Room page.
   */
  fetchTaskRouterOnly: async () => {
    set({ isFetchingTaskRouter: true });
    try {
      const taskRouter = await fetchTaskRouter(false, { optimised: true });
      const { rawActiveCalls, unifiedRecords: existingRecords } = get();

      const queueInfo = processQueueData(
        taskRouter,
        rawActiveCalls,
        existingRecords
      );

      set({
        rawTaskRouter: taskRouter,
        queueInfo,
        isFetchingTaskRouter: false,
      });
    } catch (error) {
      console.error(
        '[unifiedDashboardStore] fetchTaskRouterOnly error:',
        error
      );
      set({ isFetchingTaskRouter: false });
    }
  },

  /**
   * Toggle waiting room table visibility
   */
  toggleWaitingRoomTable: () => {
    set((state) => ({
      isWaitingRoomTableVisible: !state.isWaitingRoomTableVisible,
    }));
  },

  /**
   * Set active panel tab
   */
  setActivePanelTab: (tab) => {
    set({ activePanelTab: tab });
  },

  // ---------------------------------------------------------------------------
  // Selectors (computed values)
  // ---------------------------------------------------------------------------

  /**
   * Get records filtered by status
   */
  getRecordsByStatus: (status) => {
    return get().unifiedRecords.filter((r) => r.overallStatus === status);
  },

  /**
   * Get records with active calls only
   */
  getActiveCallRecords: () => {
    return get().unifiedRecords.filter((r) => r.hasActiveCall);
  },

  /**
   * Get records currently in the waiting room only
   */
  getWaitingRoomRecords: () => {
    return get().unifiedRecords.filter((r) =>
      isWaitingRoomStatus(r.waitingRoom?.apptStatus)
    );
  },

  /**
   * Get queue info filtered by queue names
   */
  getFilteredQueueInfo: (queueNames) => {
    if (!queueNames || queueNames.length === 0) {
      return get().queueInfo;
    }
    return get().queueInfo.filter((q) => queueNames.includes(q.name));
  },

  /**
   * Get summary statistics
   */
  getSummary: () => {
    const { unifiedRecords, queueInfo, rawTaskRouter } = get();

    // Active calls count - from TaskRouter tasks (source of truth for task status)
    // Using TaskRouter instead of rawActiveCalls because TwilioES (Event Stream)
    // often has stale/incorrect statuses that get corrected by TaskRouter enrichment
    // in mergeAllData (Step 0 & Step 2.5). This ensures the count matches what's
    // actually displayed in Queue Info and Unified Dashboard.
    // Exclude 'wrapping' from the count as those calls are no longer actively in progress.
    const activeCallCount = (rawTaskRouter?.tasks || []).filter(
      (task) =>
        ACTIVE_CALL_STATUSES.includes(task.assignmentStatus) &&
        task.assignmentStatus !== 'wrapping'
    ).length;

    // Waiting count - patients still in the waiting room
    const waitingCount = unifiedRecords.filter((r) =>
      isWaitingRoomStatus(r.waitingRoom?.apptStatus)
    ).length;

    // With Doctor count - patients currently with doctor/provider
    const withDoctorCount = unifiedRecords.filter((r) => {
      // Check call status for assigned (connected to agent/doctor)
      if (r.hasActiveCall && r.call) {
        return r.call.status === 'assigned';
      }
      return isWithProviderStatus(r.waitingRoom?.apptStatus);
    }).length;

    // SLA breach count - waiting-room patients at or above their SLA threshold
    const slaBreachCount = unifiedRecords.filter((r) => {
      return (
        isWaitingRoomStatus(r.waitingRoom?.apptStatus) &&
        r.slaStatus?.percentage &&
        r.slaStatus.percentage >= 100
      );
    }).length;

    return {
      totalRecords: unifiedRecords.length,
      activeCalls: activeCallCount,
      waiting: waitingCount,
      withDoctor: withDoctorCount,
      slaBreaches: slaBreachCount,
      totalQueues: queueInfo.length,
    };
  },
}));

export default useUnifiedDashboardStore;
